import React, { useEffect, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Avatar, 
  Button,
  Menu,
  MenuItem,
  Divider,
  Badge,
  LinearProgress,
  Chip,
  Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PersonIcon from '@mui/icons-material/Person';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { styled } from '@mui/material/styles';
import apiService from '../services/api';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#333',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

const NavItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'disabled',
})(({ theme, active, disabled }) => ({
  margin: '0 16px',
  fontWeight: active ? 600 : 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? theme.palette.text.disabled : (active ? theme.palette.primary.main : 'inherit'),
  borderBottom: active ? `2px solid ${theme.palette.primary.main}` : 'none',
  paddingBottom: '4px',
  opacity: disabled ? 0.55 : 1,
  '&:hover': {
    color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
  },
}));

const formatStorageSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let nextSize = size;
  let unitIndex = 0;
  while (nextSize >= 1024 && unitIndex < units.length - 1) {
    nextSize /= 1024;
    unitIndex += 1;
  }
  return `${nextSize.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const Header = ({
  currentView = 'dashboard',
  currentProject = null,
  onNavigate,
  user,
  onLogout,
  notifications = [],
  onNotificationOpen,
  onNotificationRead,
  onNotificationsClear,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [storageUsage, setStorageUsage] = useState(null);
  const open = Boolean(anchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const defaultMenuSettings = { dashboard: true, archive: true, elevations: true, reports: true, buildings: true, settings: true };
  const menuSettings = (() => {
    try {
      const s = localStorage.getItem('menu_settings');
      return s ? { ...defaultMenuSettings, ...JSON.parse(s) } : defaultMenuSettings;
    } catch {
      return defaultMenuSettings;
    }
  })();

  useEffect(() => {
    let cancelled = false;

    const loadStorageUsage = async () => {
      try {
        const data = await apiService.getStorageUsage();
        if (!cancelled) setStorageUsage(data);
      } catch (error) {
        if (!cancelled) setStorageUsage(null);
      }
    };

    loadStorageUsage();
    const timer = window.setInterval(loadStorageUsage, 60000);
    window.addEventListener('storage-usage-refresh', loadStorageUsage);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('storage-usage-refresh', loadStorageUsage);
    };
  }, []);

  const buildingContext = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const buildingIdParam = params.get('buildingId');
      const projectIdParam = params.get('projectId');
      const projectNameParam = params.get('projectName');
      const buildingId = buildingIdParam ? parseInt(buildingIdParam, 10) : null;
      const projectId = projectIdParam ? parseInt(projectIdParam, 10) : null;
      return {
        hasContext: Boolean(buildingId && !Number.isNaN(buildingId) && projectId && !Number.isNaN(projectId)),
        buildingId,
        projectId,
        projectName: projectNameParam || null,
      };
    } catch {
      return { hasContext: false, buildingId: null, projectId: null, projectName: null };
    }
  })();

  const handleNavClick = (view) => {
    if ((view === 'elevations' || view === 'editor' || view === 'reports') && !hasImageManagerContext) return;
    if (onNavigate) {
      onNavigate(view);
    }
  };

  const handleBuildingInfoClick = () => {
    if (!onNavigate) return;
    if (!buildingContext.hasContext) return;
    onNavigate('buildingInfo', {
      buildingId: buildingContext.buildingId,
      projectId: buildingContext.projectId,
      ...(buildingContext.projectName ? { projectName: buildingContext.projectName } : {}),
    });
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    if (onNotificationOpen) {
      onNotificationOpen();
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (onLogout) {
      onLogout();
    }
  };

  const handleClearCache = () => {
    try {
      // Clear annotation-related cache keys
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('annotation_') || key.startsWith('annotations_')) {
          localStorage.removeItem(key);
        }
      });
      // Optionally clear folder structure cache if any custom keys were used
      if (localStorage.getItem('folder_structure')) {
        localStorage.removeItem('folder_structure');
      }
      // Force reload to fetch fresh counts from backend
      window.location.reload();
    } catch (e) {
      // Fallback: still reload to ensure UI refresh
      window.location.reload();
    }
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const unreadCount = (notifications || []).filter((item) => !item.read).length;
  const hasImageManagerContext = Boolean(currentProject?.projectId && currentProject?.buildingId);
  const storagePercent = storageUsage?.exists ? Number(storageUsage.percent_used || 0) : 0;
  const storageTooltip = storageUsage?.exists
    ? `${formatStorageSize(storageUsage.used)} of ${formatStorageSize(storageUsage.total)} used · ${formatStorageSize(storageUsage.free)} available`
    : 'Storage usage unavailable';
  const sortedNotifications = [...(notifications || [])].sort((a, b) => {
    const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  const getNotificationIcon = (notification) => {
    const color =
      notification.status === 'failed'
        ? 'error'
        : notification.status === 'completed'
          ? 'success'
          : notification.status === 'running'
            ? 'primary'
            : 'info';

    const icon =
      notification.status === 'failed'
        ? <ErrorOutlineIcon fontSize="small" />
        : notification.status === 'completed'
          ? <CheckCircleIcon fontSize="small" />
          : notification.status === 'running'
            ? <HourglassTopIcon fontSize="small" />
            : <NotificationsActiveIcon fontSize="small" />;

    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: `${color}.main`,
          backgroundColor: `${color}.light`,
        }}
      >
        {icon}
      </Box>
    );
  };

  const formatNotificationTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const renderNotificationDetail = (label, value) => {
    if (!value) return null;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, wordBreak: 'break-word' }}>
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {label}:
        </Box>{' '}
        {value}
      </Typography>
    );
  };

  const renderReportDetails = (notification) => {
    if (!notification || notification.type !== 'report') return null;

    const inferReportFormat = (item) => {
      const direct = String(item?.format || '').trim().toUpperCase();
      if (direct) return direct;
      const candidates = [
        item?.file_url,
        item?.filename,
        item?.reportName,
        item?.title,
        item?.message,
      ].filter(Boolean);
      for (const candidate of candidates) {
        const text = String(candidate).trim();
        const upper = text.toUpperCase();
        if (upper.endsWith('.PDF') || upper.includes('PDF')) return 'PDF';
        if (upper.endsWith('.DOCX') || upper.includes('DOCX') || upper.includes('WORD')) return 'DOCX';
        if (upper.endsWith('.XLSX') || upper.includes('XLSX') || upper.includes('EXCEL')) return 'XLSX';
      }
      return '';
    };

    const reportFormat = inferReportFormat(notification);
    const formatIcon =
      reportFormat === 'PDF'
        ? <PictureAsPdfIcon sx={{ fontSize: 14 }} />
        : reportFormat === 'DOCX'
          ? <DescriptionIcon sx={{ fontSize: 14 }} />
          : reportFormat === 'XLSX'
            ? <TableChartIcon sx={{ fontSize: 14 }} />
            : null;

    const details = [
      { label: 'Project', value: notification.projectName },
      { label: 'Building', value: notification.buildingName },
      { label: 'Report Name', value: notification.reportName || notification.filename },
    ].filter((item) => item.value);
    const statusLabel =
      notification.status === 'completed'
        ? 'Ready to open'
        : notification.status === 'failed'
          ? 'Failed'
          : 'Generating report';

    if (details.length === 0 && notification.status !== 'completed') return null;

    return (
      <Box
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 1.5,
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          border: '1px solid rgba(25, 118, 210, 0.14)',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
          Report details
        </Typography>
        {reportFormat ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
            <Chip
              size="small"
              icon={formatIcon}
              label={reportFormat}
              variant="outlined"
              sx={{
                height: 22,
                fontWeight: 700,
                '& .MuiChip-label': { px: 1 },
                '& .MuiChip-icon': { ml: 0.75 },
              }}
            />
          </Box>
        ) : null}
        {details.map((item) => (
          <Typography key={item.label} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, wordBreak: 'break-word' }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {item.label}:
            </Box>{' '}
            {item.value}
          </Typography>
        ))}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            fontWeight: 700,
            color:
              notification.status === 'failed'
                ? 'error.main'
                : notification.status === 'completed'
                  ? 'success.main'
                  : 'primary.main',
          }}
        >
          {statusLabel}
        </Typography>
      </Box>
    );
  };
  
  return (
    <StyledAppBar position="static">
      <Toolbar>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" color="primary" fontWeight="bold">
            <span style={{ color: '#1976d2' }}>Image Manager</span>
          </Typography>
        </Box>
        
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          {menuSettings.dashboard && (
            <NavItem 
              variant="body1" 
              active={currentView === 'dashboard'}
              onClick={() => handleNavClick('dashboard')}
            >
              Dashboard
            </NavItem>
          )}
          {menuSettings.archive && (
            <NavItem
              variant="body1"
              active={currentView === 'archive'}
              onClick={() => handleNavClick('archive')}
            >
              Archive
            </NavItem>
          )}
          {menuSettings.elevations && (
            <NavItem 
              variant="body1" 
              active={currentView === 'elevations' || currentView === 'editor'}
              disabled={!hasImageManagerContext}
              aria-disabled={!hasImageManagerContext}
              onClick={() => handleNavClick('elevations')}
            >
              Image Manager
            </NavItem>
          )}
          {menuSettings.buildings && buildingContext.hasContext && (
            <NavItem
              variant="body1"
              active={currentView === 'buildingInfo'}
              onClick={handleBuildingInfoClick}
            >
              Building Info
            </NavItem>
          )}
          {menuSettings.reports && (
            <NavItem 
              variant="body1" 
              active={currentView === 'reports'}
              disabled={!hasImageManagerContext}
              aria-disabled={!hasImageManagerContext}
              onClick={() => handleNavClick('reports')}
            >
              Reports
            </NavItem>
          )}
          {menuSettings.settings && (
            <NavItem 
              variant="body1"
              active={currentView === 'settings'}
              onClick={() => handleNavClick('settings')}
            >
              Settings
            </NavItem>
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={storageTooltip} arrow>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.45,
                border: '1px solid rgba(25, 118, 210, 0.22)',
                borderRadius: 999,
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                minWidth: 118,
              }}
            >
              <CloudQueueIcon sx={{ fontSize: 17, color: 'primary.main' }} />
              <Box sx={{ flex: 1, minWidth: 58 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1, color: 'primary.main' }}>
                  {storageUsage?.exists ? `${storagePercent}%` : '-'} Storage
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, storagePercent)}
                  sx={{
                    mt: 0.45,
                    height: 4,
                    borderRadius: 999,
                    backgroundColor: 'rgba(25, 118, 210, 0.14)',
                    '& .MuiLinearProgress-bar': { borderRadius: 999 },
                  }}
                />
              </Box>
            </Box>
          </Tooltip>
          <IconButton onClick={handleNotificationsClick} aria-label="notifications">
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton onClick={handleAvatarClick}>
            <Avatar sx={{ width: 32, height: 32, ml: 1 }}>
              {getUserInitials(user)}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleMenuClose}>
              <Avatar sx={{ mr: 2 }}>
                {getUserInitials(user)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || 'user@example.com'}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <PersonIcon sx={{ mr: 2 }} />
              Profile
            </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 2 }} />
            Logout
          </MenuItem>
          <MenuItem onClick={handleClearCache}>
            <DeleteSweepIcon sx={{ mr: 2 }} />
            Clear Cache
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={notificationsAnchorEl}
          open={notificationsOpen}
          onClose={handleNotificationsClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.22))',
              mt: 1.5,
              width: 380,
              maxWidth: 'calc(100vw - 32px)',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 58,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Notifications
            </Typography>
            <Button
              size="small"
              onClick={() => {
                if (onNotificationsClear) onNotificationsClear();
                handleNotificationsClose();
              }}
            >
              Clear all
            </Button>
          </Box>
          <Divider />
          <Box sx={{ p: 1, maxHeight: 420, overflowY: 'auto' }}>
            {sortedNotifications.length > 0 ? (
              sortedNotifications.map((notification) => (
                <Box
                  key={notification.id}
                  onClick={() => {
                    if (onNotificationRead) {
                      onNotificationRead(notification.id, notification);
                    }
                  }}
                  sx={{
                    mb: 1,
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: notification.read ? 'transparent' : 'rgba(25, 118, 210, 0.06)',
                    border: '1px solid',
                    borderColor: notification.read ? 'rgba(0,0,0,0.08)' : 'rgba(25, 118, 210, 0.18)',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    {getNotificationIcon(notification)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {notification.title}
                        </Typography>
                        {!!formatNotificationTime(notification.updatedAt || notification.createdAt) && (
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatNotificationTime(notification.updatedAt || notification.createdAt)}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {notification.message}
                      </Typography>
                      {notification.type === 'report' ? (
                        renderReportDetails(notification)
                      ) : (
                        <>
                          {renderNotificationDetail('Project', notification.projectName)}
                          {renderNotificationDetail('Building', notification.buildingName)}
                          {renderNotificationDetail('File', notification.filename)}
                        </>
                      )}
                      {notification.status === 'running' && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, notification.progress || 0))}
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {Math.min(100, Math.max(0, Math.round(notification.progress || 0)))}% generating
                          </Typography>
                        </Box>
                      )}
                      {notification.status !== 'running' && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            label={notification.status === 'completed' ? 'Completed' : notification.status === 'failed' ? 'Failed' : 'Info'}
                            color={notification.status === 'completed' ? 'success' : notification.status === 'failed' ? 'error' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ px: 1.5, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No notifications yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Menu>
      </Box>
    </Toolbar>
  </StyledAppBar>
  );
};

export default Header;
